import { ReactNode, useCallback, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import {
  selectPayrollCompany,
  selectPayrollProfile,
} from '../payroll.selectors'
import {
  GEP_ENROLL_PATHS,
  getResumeLink,
  ONBOARDING_STEPS,
  useOnboardingSteps,
  useUpdatePayrollOnboardingStep,
} from '../helpers'
import { PayrollSetupList } from './PayrollSetupList'
import { putApproveDemoCompany } from '../payrollActions'
import {
  Card,
  Text,
  Button,
  Image,
  Link,
  Icon,
  CardColors,
  Separator,
} from '@/ui'
import { useAnalyticsView } from '@/features/amplitude'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useFetchResponseWithUtils, useReselector } from '@/utils/sharedHooks'
import { CreatePayrollDevAccount } from '../CreatePayrollDevAccount'
import { getCurrentUser } from '@/selectors/user.selectors'
import { PayrollSetup } from '@/reducers/auth/userReducer'
import { PayrollIntroCard } from '../upsell/WelcomeToPayroll'
import { getMostRecentPayrollInterestSurvey } from '../upsell/payrollInterestSurvey.actions'
import { isProduction } from '@/utils/envHelpers'
import { TextColors, TypographyStyles } from '@/styles'

// TODO: Migrate to V2
import { CircularWrapper } from '@/base'
import { ProductFeatureId } from '@/actions/permissionActions'
import { selectUserHasPermission } from '@/reducers/auth/permission.selectors'

const NextStep1 = () => {
  const user = useReselector(getCurrentUser)
  const latestPayrollSurvey = useFetchResponseWithUtils(
    getMostRecentPayrollInterestSurvey
  )

  const payrollSetup =
    latestPayrollSurvey.result?.payrollSetup || user?.payrollSetup

  if (payrollSetup === PayrollSetup.gusto) {
    return (
      <>
        <Text variant={TypographyStyles.H3}>Connect to Gusto</Text>
        <Text variant={TypographyStyles.BODY_MD}>
          After clicking Enroll in Payroll, you’ll be prompted to login to
          Gusto. Once you accept the terms, your Gusto information will
          automatically sync with Heard.
        </Text>
      </>
    )
  }

  return (
    <>
      <Text variant={TypographyStyles.H3}>Complete payroll onboarding</Text>
      <Text variant={TypographyStyles.BODY_MD}>This takes 30-45 minutes.</Text>
    </>
  )
}

const Step2 = () => {
  const user = useReselector(getCurrentUser)
  const latestPayrollSurvey = useFetchResponseWithUtils(
    getMostRecentPayrollInterestSurvey
  )

  const payrollSetup =
    latestPayrollSurvey.result?.payrollSetup || user?.payrollSetup

  return (
    <>
      <Text variant={TypographyStyles.H3}>
        Start managing your payroll in Heard
      </Text>
      <Text variant={TypographyStyles.BODY_MD}>
        Your payroll should now be active in Heard.
        {payrollSetup === PayrollSetup.other
          ? ' Export your statements from your previous provider and upload them into Gusto.'
          : ''}
      </Text>

      {payrollSetup === PayrollSetup.gusto && (
        <>
          <br />
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
            Important: Migration fully transfers your payroll to Heard. Your
            Gusto payroll will no longer be available, nor will they be able to
            provide support. Please reach out to us via Messages instead.
          </Text>
        </>
      )}
      <br />
      <Link
        href="https://support.joinheard.com/hc/en-us/sections/7709867454359-Payroll"
        newPage
      >
        Heard Payroll help articles
        <Icon icon={faArrowRight} className="ml-2" />
      </Link>
    </>
  )
}

const FreeToEnroll = ({ enrollButton }: { enrollButton: ReactNode }) => {
  return (
    <div className="flex flex-row gap-10">
      <div className="flex flex-col gap-6 w-full">
        <Text variant={TypographyStyles.H1}>Enroll in Heard Payroll</Text>

        <Card
          color={CardColors.LEVEL_1}
          className="flex justify-center items-center bg-orange-25"
        >
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/pencil.svg"
            alt="Pencil Icon"
            className="w-[180px] h-[180px] bg-transparent"
          />
        </Card>

        <Text variant={TypographyStyles.H2}>Next steps</Text>

        <div className="flex flex-col gap-6">
          <div className="flex items-start gap-4">
            <div className="flex justify-end w-[40px]">
              <CircularWrapper height={28} wrapperColor="stone">
                <Text variant={TypographyStyles.H3}>1</Text>
              </CircularWrapper>
            </div>
            <div className="flex-1">
              <NextStep1 />
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex justify-end w-[40px]">
              <CircularWrapper height={28} wrapperColor="stone">
                <Text variant={TypographyStyles.H3}>2</Text>
              </CircularWrapper>
            </div>
            <div className="flex-1">
              <Step2 />
            </div>
          </div>
        </div>

        <div>{enrollButton}</div>

        <CreatePayrollDevAccount />
      </div>
    </div>
  )
}

export const EnrollmentIntro = ({
  enrollButton,
}: {
  enrollButton: ReactNode
}) => {
  const hasFreePayrollWithSubscription = useReselector(
    selectUserHasPermission,
    ProductFeatureId.payrollIncludedForFreeForSelf
  )

  if (hasFreePayrollWithSubscription) {
    return <FreeToEnroll enrollButton={enrollButton} />
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="mt-10" />

      <Text
        variant={TypographyStyles.DISPLAY_SM}
        color={TextColors.BRAND}
        className="text-center"
      >
        Enroll in Payroll with Heard
      </Text>

      <Text
        variant={TypographyStyles.BODY_MD}
        className="text-center max-w-3xl mx-auto"
      >
        Run payroll for your employees, contractors and even yourself, all
        within Heard’s platform.
      </Text>

      <div className="flex flex-row gap-6 w-full mt-6">
        <div className="w-1/6" />
        <div className="w-2/3">
          <PayrollIntroCard />
        </div>

        <div className="w-1/2 flex flex-col gap-6">
          <Text variant={TypographyStyles.H2}>Next steps</Text>

          <div className="flex items-start gap-4">
            <div className="w-[40px] flex justify-end">
              <CircularWrapper height={28} wrapperColor="stone">
                <Text variant={TypographyStyles.H3}>1</Text>
              </CircularWrapper>
            </div>
            <div className="flex-1">
              <NextStep1 />
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-[40px] flex justify-end">
              <CircularWrapper height={28} wrapperColor="stone">
                <Text variant={TypographyStyles.H3}>2</Text>
              </CircularWrapper>
            </div>
            <div className="flex-1">
              <Step2 />
            </div>
          </div>

          <div>{enrollButton}</div>
        </div>
      </div>

      <div className="mt-10" />
      <CreatePayrollDevAccount />
    </div>
  )
}

export const PayrollEnroll = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const payrollProfile = useReselector(selectPayrollProfile)
  const payrollCompany = useReselector(selectPayrollCompany)
  const steps = useOnboardingSteps()
  const pageView = useAnalyticsView()
  const nextPageLink = getResumeLink(steps)
  useUpdatePayrollOnboardingStep(ONBOARDING_STEPS.enrollment)

  const approveCompany = useCallback(
    () => putApproveDemoCompany()(dispatch),
    [dispatch]
  )

  useEffect(() => {
    // If company is approved auto navigate to run payroll page
    if (payrollCompany?.company_status === 'Approved') {
      navigate('/payroll/run_payroll')
    }
    pageView('payroll enroll')
  }, [navigate, payrollCompany?.company_status, pageView])

  const enrollButton = useMemo(
    () => (
      <Button onClick={() => navigate(nextPageLink)}>
        {nextPageLink === GEP_ENROLL_PATHS.overview
          ? 'Enroll in Payroll'
          : 'Resume Enrollment'}
      </Button>
    ),
    [navigate, nextPageLink]
  )

  if (payrollProfile?.onboardingCompleted) {
    return (
      <div className="flex flex-col gap-6">
        <Text variant={TypographyStyles.H1}>Welcome to Heard Payroll!</Text>
        <Separator />

        <div className="flex flex-row gap-6">
          <div className="w-1/2">
            <PayrollSetupList />
          </div>
          <div className="w-1/2 flex flex-col items-center text-center gap-6 mt-6">
            <Text variant={TypographyStyles.H1}>
              You&apos;re all set up! We&apos;ll notify you when payroll is
              ready to be run.
            </Text>
            <Image
              src="https://heard-images.s3.us-west-1.amazonaws.com/assets/payroll_rocket.png"
              alt="Payroll Rocket"
              width={300}
            />
          </div>
        </div>

        {!isProduction() && (
          <div className="w-1/2 mt-6">
            <Button onClick={approveCompany}>Approve demo company</Button>
          </div>
        )}
      </div>
    )
  }

  return <EnrollmentIntro enrollButton={enrollButton} />
}
